/**
 * @file jwtCookie.ts
 * @description Utilities for managing JWT cookies for NodeBB session sharing
 */

export interface JWTCookieOptions {
  domain?: string;
  maxAge?: number; // in seconds
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

const DEFAULT_OPTIONS: JWTCookieOptions = {
  domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || 
          (process.env.NODE_ENV === 'production' ? '.hirecarta.com' : 'localhost'),
  maxAge: 30 * 24 * 60 * 60, // 30 days in seconds
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax'
};

/**
 * Set JWT cookie for cross-domain session sharing
 */
export function setJWTCookie(token: string, options: JWTCookieOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const cookieString = [
    `hirecarta_jwt=${token}`,
    `domain=${opts.domain}`,
    'path=/',
    `max-age=${opts.maxAge}`,
    opts.secure ? 'secure' : '',
    `samesite=${opts.sameSite}`
  ].filter(Boolean).join('; ');
  
  if (typeof document !== 'undefined') {
    document.cookie = cookieString;
  }
}

/**
 * Clear JWT cookie across all domains
 */
export function clearJWTCookie(options: JWTCookieOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const expiredCookieString = [
    'hirecarta_jwt=',
    `domain=${opts.domain}`,
    'path=/',
    'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  ].join('; ');
  
  if (typeof document !== 'undefined') {
    document.cookie = expiredCookieString;
  }
}

/**
 * Get JWT from cookie (client-side)
 */
export function getJWTFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  
  const name = 'hirecarta_jwt=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  
  for (let cookie of cookieArray) {
    cookie = cookie.trim();
    if (cookie.indexOf(name) === 0) {
      const jwt = cookie.substring(name.length);
      return jwt;
    }
  }
  
  return null;
}

/**
 * Server-side cookie parsing (for API routes)
 */
export function parseJWTFromServerCookie(cookieHeader: string | undefined): string | null {
  if (!cookieHeader) return null;
  
  const cookies = cookieHeader.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = value;
    return acc;
  }, {} as Record<string, string>);
  
  return cookies.hirecarta_jwt || null;
}
